/**
 * recovr — buy buttons
 *
 * Wraps the buy-box add-to-cart form and keeps it in sync with the plan
 * selector WITHOUT depending on DOM order or a parent/child relationship
 * (both blocks can be reordered or removed independently).
 *
 * It listens for the `recovr:plan:change` event the plan selector dispatches
 * on the section and writes the chosen variant id + selling_plan onto THIS
 * form. The plan selector also writes the form directly (its proven,
 * Seal-resistant path) — the two are idempotent. With no plan block on the
 * page, no event fires and the server-rendered variant id (the first
 * available variant) is what posts, so add-to-cart still works.
 */
class RecovrBuyButtons extends HTMLElement {
  connectedCallback() {
    /** @type {{variantId?: string, sellingPlanId?: string}} */
    this.state = {};
    this.form = this.querySelector('form[data-type="add-to-cart-form"]');

    const scope = this.closest('.shopify-section') || document;
    // capture so we still catch it if the plan block sits later in the DOM
    scope.addEventListener('recovr:plan:change', this.#onPlanChange, true);
    this._scope = scope;
  }

  disconnectedCallback() {
    this._scope?.removeEventListener('recovr:plan:change', this.#onPlanChange, true);
  }

  /** @param {CustomEvent} event */
  #onPlanChange = (event) => {
    const detail = event.detail || {};
    if (detail.productId && this.dataset.productId && String(detail.productId) !== String(this.dataset.productId)) return;
    this.state = detail;
    this.#apply();
  };

  #apply() {
    if (!this.form) return;
    const { variantId, sellingPlanId } = this.state;

    if (variantId) {
      const idField = this.form.querySelector('input[name="id"]');
      if (idField && idField.value !== String(variantId)) idField.value = String(variantId);
    }

    const planId = (sellingPlanId ?? '').toString().split('/').pop() ?? '';
    let planField = this.form.querySelector('input[name="selling_plan"]');
    if (planId) {
      if (!planField) {
        planField = document.createElement('input');
        planField.type = 'hidden';
        planField.name = 'selling_plan';
        this.form.appendChild(planField);
      }
      planField.value = planId;
    } else if (planField) {
      planField.remove();
    }
  }
}

if (!customElements.get('recovr-buy-buttons')) {
  customElements.define('recovr-buy-buttons', RecovrBuyButtons);
}
